// @flow
// The record's text field with the rich-text editor on (RecordElement, only for a doctor whose
// advancedData.richTextEditor.enabled is true; everyone else gets the plain textarea exactly as it always was, and
// nothing here runs for them): the editor, loaded on demand (its own chunk), with the record's text as the timeline
// shows it.
// A record the editor can't give back as it is (readRecord: <p> lines, a link, an image, a table…) opens in the plain
// textarea exactly as it always did: same text, no hint. So does every record when reading it fails.
// While the editor's code loads, the field shows the record as it will show in it, read-only: a tap there says the
// editor is on its way, and the editor (or the textarea instead) takes the focus when it comes, if the field still has
// it. A record with the editor's formatting stays so
// until the code is in, never in the textarea as text without its formatting: with "Atualize a página…" and a retry
// when the code didn't load. A record without formatting goes to the textarea when the code is late (EDITOR_LOAD_WAIT)
// or didn't load, and stays there while it is open. The editor breaking: the textarea with the text as the textarea
// always showed it (a formatted record's tags as text, kept when edited there).
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { TextArea, Icon } from '@elements';
import { replaceHtmlEntitesToText } from '@helpers/helpers';
import { readRecord, TAG_OF_MARK } from './recordText';
import type { EditorDoc } from './recordText';
import { importEditor } from './editorChunk';
import { ToolbarButtons } from './toolbar';
import { reportEditorFailure } from './editorReports';
import './RecordTextField.scss';

// ---------------------------------------------------------------------------------------------------------------------
// The editor's code, on demand. After a deploy the page's old chunk is gone (gulp publish deletes the old hashed
// files): a failed load is tried again once at once, and again the next time a record opens (or on "Tentar de novo"),
// never kept as failed.

let editorModule = null;
let editorLoading = null;

export const loadRichTextEditor = (): Promise<any> => {
  if (editorModule) return Promise.resolve(editorModule);
  if (!editorLoading) {
    editorLoading = importEditor().catch(() => importEditor()).then((module) => {
      editorModule = module;
      return module;
    }, (error) => {
      editorLoading = null;
      throw error;
    });
  }
  return editorLoading;
};

// Starts loading the editor before it is needed (the timeline of a user with the editor on), so opening a record
// shows it at once. Loading it twice is one request.
export const preloadRichTextEditor = () => {
  loadRichTextEditor().catch(() => {});
};

// How long a record without formatting waits for the editor's code (a slow network) before it opens in the textarea
// instead; a record with formatting says it is still loading
export const EDITOR_LOAD_WAIT = 4000;

// The editor's code for this field: `module` once it is in; `failed` it didn't load (`retry` loads it again); `slow`
// it wasn't in after EDITOR_LOAD_WAIT
type EditorState = { module: any, failed: boolean, slow: boolean, retry: () => void };
const useEditorModule = (wanted: boolean): EditorState => {
  const [state, setState] = useState(() => ({ module: editorModule, failed: false, slow: false }));
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!wanted || state.module) return undefined;
    let current = true;
    const timer = setTimeout(() => {
      if (current) setState((was) => ({ ...was, slow: true }));
    }, EDITOR_LOAD_WAIT);
    loadRichTextEditor().then((module) => {
      clearTimeout(timer);
      if (current) setState({ module, failed: false, slow: false });
    }, (error) => {
      clearTimeout(timer);
      reportEditorFailure('load', error);
      if (current) setState((was) => ({ ...was, failed: true }));
    });
    return () => {
      current = false;
      clearTimeout(timer);
    };
  // When the field opens, and on each retry
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);
  const retry = useCallback(() => {
    setState((was) => ({ ...was, failed: false, slow: false }));
    setAttempt((count) => count + 1);
  }, []);
  return { ...state, retry };
};

// ---------------------------------------------------------------------------------------------------------------------

// The editor broke while it showed (or wouldn't take the record's text): the plain textarea instead (fallback), with
// the text as it stood then. Bugsnag gets the error's class only (editorReports).
class EditorBoundary extends React.Component<{ children: any, fallback: () => any }, { failed: boolean }> {
  static getDerivedStateFromError() {
    return { failed: true };
  }

  constructor(props: { children: any, fallback: () => any }) {
    super(props);
    this.state = { failed: false };
  }

  componentDidCatch(error: any) {
    reportEditorFailure('render', error);
  }

  render() {
    return this.state.failed ? this.props.fallback() : this.props.children;
  }
}

// The record as the editor will show it (its document, as React elements): its lines, marks and lists
const inlineView = (nodes: Array<any> = []) => nodes.map((node, index) => {
  if (node.type === 'hardBreak') return <br key={String(index)} />; // eslint-disable-line react/no-array-index-key
  const shown = (node.marks || []).reduceRight((inner, mark) => {
    const Tag = TAG_OF_MARK[mark.type];
    return Tag ? <Tag>{inner}</Tag> : inner;
  }, node.text || '');
  return <React.Fragment key={String(index)}>{shown}</React.Fragment>; // eslint-disable-line react/no-array-index-key
});
const blocksView = (blocks: Array<any> = []) => blocks.map((block, index) => {
  if (block.type === 'paragraph') {
    // eslint-disable-next-line react/no-array-index-key
    return <p key={String(index)}>{block.content && block.content.length ? inlineView(block.content) : <br />}</p>;
  }
  const List = block.type === 'orderedList' ? 'ol' : 'ul';
  return (
    // eslint-disable-next-line react/no-array-index-key
    <List key={String(index)}>
      {/* eslint-disable-next-line react/no-array-index-key */}
      {(block.content || []).map((item, n) => <li key={String(n)}>{blocksView(item.content)}</li>)}
    </List>
  );
});
const isEmptyDoc = (doc: EditorDoc): boolean => doc.content.length === 1 && doc.content[0].type === 'paragraph'
  && !(doc.content[0].content && doc.content[0].content.length);

type LoadingProps = {
  doc: EditorDoc,
  placeholder: string,
  waiting: boolean, // tapped or focused: says the editor is on its way
  readOnly?: boolean, // the record finalized or deleted
  onWant: () => void,
  hadFocus: { current: boolean },
  _: Function,
};

// What shows while the editor loads: the field with its toolbar and the record as it will show, read-only. It takes
// the focus (a tap, Tab), and then says the editor is on its way, with no caret: nothing typed there can be kept.
// While the record is finalized or deleted it takes no focus and says nothing. Leaving (the editor, or the textarea
// instead, came), it tells whether it still had the focus.
const Loading = ({
  doc, placeholder, waiting, readOnly, onWant, hadFocus, _,
}: LoadingProps) => {
  const ref = useRef(null);
  useLayoutEffect(() => () => {
    const node = ref.current;
    // eslint-disable-next-line no-param-reassign
    if (node && node.contains(document.activeElement)) hadFocus.current = true;
  }, [hadFocus]);
  const empty = isEmptyDoc(doc);
  return (
    <div className={waiting && !readOnly ? 'recordEditor loading waiting' : 'recordEditor loading'} aria-busy="true" ref={ref}>
      <ToolbarButtons _={_} disabled />
      {/* Read-only: not focusable, and no handlers either */}
      {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus */}
      <div
        className={empty ? 'recordEditorContent empty' : 'recordEditorContent'}
        data-placeholder={placeholder}
        role="textbox"
        aria-multiline="true"
        aria-readonly="true"
        aria-label={placeholder}
        tabIndex={readOnly ? undefined : 0}
        onFocus={readOnly ? undefined : onWant}
        onPointerDown={readOnly ? undefined : onWant}
        onKeyDown={readOnly ? undefined : onWant}
      >
        {empty ? null : blocksView(doc.content)}
      </div>
    </div>
  );
};

const Hint = ({ text, action }: { text: string, action?: { label: string, onClick: () => void } }) => (
  <p className="recordTextFormattingHint">
    <Icon iconName="info" size={16} />
    <span>{text}</span>
    {action && <button type="button" className="recordTextHintAction" onClick={action.onClick}>{action.label}</button>}
  </p>
);

// The record as the field opens it; null when reading it failed (the textarea then, as always)
const readSafely = (content: string) => {
  try {
    return readRecord(content);
  } catch (error) {
    reportEditorFailure('read', error);
    return null;
  }
};

type Props = {
  name: string,
  content: string, // the record's text when the field opens (later changes come from the field itself)
  // What the plain textarea opens with, as it always did (RecordElement's own copy)
  textareaValue?: string,
  placeholder: string,
  readOnly?: boolean,
  onChange: Function,
  lockRef?: { current: ?() => void }, // the editor's lock (RichTextEditor)
  _: Function,
};

const RecordTextField = ({
  name, content, textareaValue, placeholder, readOnly, onChange, lockRef, _,
}: Props) => {
  // Read once, when the field opens: what is typed in it never changes which field it is
  const [opened] = useState(() => readSafely(content));
  const editor = useEditorModule(Boolean(opened && opened.editable));
  // The editor's text, as it reads it (its last onChange): where its fallback starts
  const editorText = useRef(null);
  const fallbackText = useRef(null);
  // A record without formatting in the textarea for good, once the editor's code was late or didn't load (its hints)
  const inTextarea = useRef(null);
  const handleEditorChange = useCallback((change: Object) => {
    editorText.current = change.value;
    onChange(change);
  }, [onChange]);
  // The field tapped or focused while the editor loaded: the editor (or the textarea instead) takes the focus when it
  // comes, at the end of the text, only if the field itself still had it then (not once the user moved on)
  const [waiting, setWaiting] = useState(false);
  const hadFocus = useRef(false);
  const handleWant = useCallback(() => setWaiting(true), []);
  const focusOnOpen = useCallback((): boolean => hadFocus.current, []);
  // The textarea instead of the loading field that had the focus: it takes it, at the end of the text
  const textareaRef = useRef(null);
  useLayoutEffect(() => {
    if (!inTextarea.current || !hadFocus.current) return;
    hadFocus.current = false;
    const element = textareaRef.current && textareaRef.current.selfRef;
    if (!element || readOnly) return;
    element.focus();
    element.setSelectionRange(element.value.length, element.value.length);
  });

  const textarea = (value: string, hints: Array<string> = []) => (
    <React.Fragment>
      <TextArea
        ref={textareaRef}
        name={name}
        readOnly={readOnly}
        placeholder={placeholder}
        onChange={onChange}
        defaultValue={value}
        uncontrolled
      />
      {hints.map((hint) => <Hint key={hint} text={hint} />)}
    </React.Fragment>
  );
  const asAlways = textareaValue === undefined ? replaceHtmlEntitesToText(replaceHtmlEntitesToText(content)) : textareaValue;

  // The editor can't open it: the textarea, as always
  if (!opened || !opened.editable) return textarea(asAlways);

  // The textarea instead of the editor, with the text as it stands then (read once), as the textarea always showed
  // it: untouched, its own copy; written in the editor, that text's lines and characters, a formatting's tags as text
  // (kept when edited there, as with the editor turned off)
  const fallback = (hints: Array<string>) => {
    if (fallbackText.current === null) {
      let text = content;
      if (editorText.current) {
        try {
          text = editorText.current();
        } catch (error) {
          // The text as it opened
        }
      }
      fallbackText.current = text === content ? asAlways : replaceHtmlEntitesToText(text);
    }
    return textarea(fallbackText.current, hints);
  };

  const reload = _('label/editor/reload_to_format');
  if (!opened.formatted && !inTextarea.current && !editor.module && (editor.failed || editor.slow)) {
    inTextarea.current = editor.failed ? [reload] : [];
  }
  if (inTextarea.current) return fallback(inTextarea.current);

  if (!editor.module) {
    let hint = null;
    if (editor.failed) hint = <Hint text={reload} action={{ label: _('action/editor/retry'), onClick: editor.retry }} />;
    else if (editor.slow) hint = <Hint text={_('label/editor/loading')} />;
    else if (waiting && !readOnly) hint = <Hint text={_('label/editor/opening')} />;
    return (
      <React.Fragment>
        <Loading
          doc={opened.doc}
          placeholder={placeholder}
          waiting={waiting}
          readOnly={readOnly}
          onWant={handleWant}
          hadFocus={hadFocus}
          _={_}
        />
        {hint}
      </React.Fragment>
    );
  }

  const { default: RichTextEditor } = editor.module;
  return (
    <EditorBoundary fallback={() => fallback([])}>
      <RichTextEditor
        name={name}
        defaultValue={content}
        initialDoc={opened.doc}
        placeholder={placeholder}
        readOnly={readOnly}
        onChange={handleEditorChange}
        lockRef={lockRef}
        focusOnOpen={focusOnOpen}
        _={_}
      />
    </EditorBoundary>
  );
};

export default RecordTextField;
